// ════════════════════════════════════════════════════════════
// CostControlTab — cuenta del proyecto de una máquina
// Guardar en: src/components/CostControlTab.tsx
// ════════════════════════════════════════════════════════════
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Alert, TextField,
  Button, IconButton, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableHead, TableRow, InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { getCostControl, crearMovimientoCC, borrarMovimientoCC } from '../api';

const SUGERENCIAS_GASTO = [
  'Precio de compra', 'Transporte', 'Reacondicionamiento', 'Repuestos',
  'Horas de taller', 'Pintura', 'Inspección', 'Comisión', 'Seguro'
];
const SUGERENCIAS_INGRESO = ['Venta', 'Señal / anticipo', 'Alquiler', 'Trabajos facturados'];

const eur = (n: number) =>
  (n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

export default function CostControlTab({ maquinaId }: { maquinaId: number }) {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    tipo: 'gasto', fecha: new Date().toISOString().slice(0, 10),
    concepto: '', importe: '', notas: ''
  });
  const [guardando, setGuardando] = useState(false);

  const cargar = () => {
    getCostControl(maquinaId).then(setData)
      .catch((e: any) => setError(e.response?.data?.detail || 'Error al cargar el cost control'));
  };
  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [maquinaId]);

  const imputar = async () => {
    setGuardando(true);
    try {
      await crearMovimientoCC(maquinaId, { ...form, importe: parseFloat(form.importe) });
      setForm({ ...form, concepto: '', importe: '', notas: '' });
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al imputar el movimiento');
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async (id: number) => {
    if (!window.confirm('¿Eliminar este movimiento?')) return;
    try { await borrarMovimientoCC(id); cargar(); }
    catch (e: any) { alert(e.response?.data?.detail || 'Error'); }
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Typography color="text.secondary">Cargando...</Typography>;

  const t = data.totales;
  const sugerencias = form.tipo === 'gasto' ? SUGERENCIAS_GASTO : SUGERENCIAS_INGRESO;

  return (
    <Box>
      {/* Totales */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, bgcolor: '#fef2f2' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Gastos</Typography>
              <Typography sx={{ fontWeight: 800, color: '#dc2626' }}>{eur(t.gastos)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, bgcolor: '#f0fdf4' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Ingresos</Typography>
              <Typography sx={{ fontWeight: 800, color: '#16a34a' }}>{eur(t.ingresos)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, bgcolor: t.resultado >= 0 ? '#f0fdf4' : '#fef2f2' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Resultado</Typography>
              <Typography sx={{ fontWeight: 800, color: t.resultado >= 0 ? '#16a34a' : '#dc2626' }}>
                {eur(t.resultado)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Margen</Typography>
              <Typography sx={{ fontWeight: 800 }}>
                {t.margen_pct === null ? '—' : `${t.margen_pct}%`}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Imputar movimiento */}
      <Card sx={{ borderRadius: 2, mb: 2 }}>
        <CardContent>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Imputar movimiento</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
            {sugerencias.map(s => (
              <Chip key={s} label={s} size="small" sx={{ cursor: 'pointer' }}
                onClick={() => setForm({ ...form, concepto: s })} />
            ))}
          </Box>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select value={form.tipo} label="Tipo"
                  onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                  <MenuItem value="gasto">🔴 Gasto</MenuItem>
                  <MenuItem value="ingreso">🟢 Ingreso</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" type="date" label="Fecha"
                InputLabelProps={{ shrink: true }} value={form.fecha}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Concepto (libre)"
                value={form.concepto}
                onChange={(e) => setForm({ ...form, concepto: e.target.value })} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" type="number" label="Importe"
                value={form.importe}
                onChange={(e) => setForm({ ...form, importe: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <Button fullWidth variant="contained" startIcon={<AddIcon />}
                disabled={guardando || !form.concepto.trim() || !form.importe}
                onClick={imputar} sx={{ bgcolor: '#4f46e5' }}>
                Imputar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Movimientos */}
      {data.movimientos.length === 0 ? (
        <Alert severity="info">Sin movimientos todavía. Imputa el primero para arrancar la cuenta del proyecto.</Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Concepto</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Imputado por</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Importe</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.movimientos.map((m: any) => (
              <TableRow key={m.id}>
                <TableCell>{m.fecha}</TableCell>
                <TableCell>
                  {m.concepto}
                  {m.notas && (
                    <Typography variant="caption" color="text.secondary" display="block">{m.notas}</Typography>
                  )}
                </TableCell>
                <TableCell>{m.creador_nombre} {m.creador_apellidos || ''}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700,
                  color: m.tipo === 'gasto' ? '#dc2626' : '#16a34a' }}>
                  {m.tipo === 'gasto' ? '−' : '+'}{eur(m.importe)}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="error" onClick={() => borrar(m.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}